import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Eye, Undo2, Pencil, PencilOff } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { getEditMode, setEditMode, subscribeEditMode } from '../lib/editModeStore';
import { registerPreviewConfirm } from '../lib/previewGate';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

// Roles available in the admin "Preview As" switcher.
export const PREVIEW_ROLES = [
  { value: 'artist', label: 'Artist', home: '/artist' },
  { value: 'executive', label: 'A&R Executive', home: '/executive' },
  { value: 'scout', label: 'Scout', home: '/scout' },
  { value: 'radio_station', label: 'Radio Station', home: '/radio-station' },
  { value: 'playlist_curator', label: 'Playlist Curator', home: '/playlist-curator' },
];

// Public pages an admin can preview (banner shows "Visitor").
export const PREVIEW_PAGES = [
  { value: 'visitor', label: 'Landing Page', home: '/', slug: 'landing-page' },
  { value: 'visitor', label: 'Sign In / Create Account', home: '/auth', slug: 'auth-page' },
];

const roleLabel = (value) =>
  value === 'visitor'
    ? 'Visitor (Public Pages)'
    : PREVIEW_ROLES.find((r) => r.value === value)?.label || value;

/**
 * Fixed top banner shown on every page while an admin is in Role Preview
 * mode. Also hosts the write-confirmation dialog: while previewing,
 * any POST/PUT/PATCH/DELETE pauses here for an explicit "Submit Change"
 * before committing — so nothing is written accidentally.
 */
export default function RolePreviewBanner() {
  const { isRolePreview, previewRole, exitRolePreview } = useAuth();
  const editMode = useSyncExternalStore(subscribeEditMode, getEditMode);
  const [pending, setPending] = useState([]); // [{info, resolve}]
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  // Register the write-confirm resolver while preview mode is active.
  useEffect(() => {
    if (!isRolePreview) return undefined;
    const unregister = registerPreviewConfirm(
      (info) =>
        new Promise((resolve) => {
          setPending((q) => [...q, { info, resolve }]);
        })
    );
    return () => {
      unregister();
      // Flush anything still waiting as "cancelled".
      pendingRef.current.forEach((p) => p.resolve(false));
      setPending([]);
    };
  }, [isRolePreview]);

  const answer = useCallback((ok) => {
    setPending((q) => {
      if (q.length) q[0].resolve(ok);
      return q.slice(1);
    });
  }, []);

  if (!isRolePreview) return null;

  const current = pending[0];

  const backToAdmin = () => {
    exitRolePreview();
    // Hard nav so every role gate re-evaluates against the admin role.
    window.location.href = '/admin';
  };

  return (
    <>
      <div
        className="w-full bg-avenue-carbon border-b-2 border-avenue-red text-avenue-white flex items-center justify-between gap-3 px-4 py-2 sticky top-0 z-[100]"
        data-testid="role-preview-banner"
      >
        <div className="flex items-center gap-2.5 text-sm min-w-0">
          <Eye className="h-4 w-4 flex-shrink-0 text-avenue-red" />
          <span className="uppercase tracking-[0.14em] text-xs font-medium text-avenue-red">
            Role Preview
          </span>
          <span className="truncate">
            Viewing as <strong data-testid="role-preview-current">{roleLabel(previewRole)}</strong>
          </span>
          <span className="hidden md:inline text-avenue-white/50 text-xs">
            Your admin session is untouched · writes ask for confirmation first
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0" data-ave-ignore="true">
          <Button
            size="sm"
            variant={editMode ? 'default' : 'outline'}
            onClick={() => setEditMode(!editMode)}
            className={editMode ? 'bg-avenue-red text-white hover:bg-avenue-electric-red' : 'border-avenue-white/40 text-avenue-white hover:border-avenue-white'}
            title="Edit any text or colors on this page"
            data-testid="preview-edit-mode-toggle"
          >
            {editMode ? <Pencil className="h-3.5 w-3.5 mr-1" /> : <PencilOff className="h-3.5 w-3.5 mr-1" />}
            {editMode ? 'Edit: On' : 'Edit: Off'}
          </Button>
          <Button
            size="sm"
            onClick={backToAdmin}
            className="bg-avenue-red text-white hover:bg-avenue-electric-red"
            data-testid="role-preview-exit-btn"
          >
            <Undo2 className="h-3.5 w-3.5 mr-1" /> Back to Admin
          </Button>
        </div>
      </div>

      {/* Write confirmation — "submit before committing" */}
      <AlertDialog open={!!current}>
        <AlertDialogContent data-testid="preview-write-confirm" data-ave-ignore="true">
          <AlertDialogHeader>
            <AlertDialogTitle>Commit this change?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  You&apos;re previewing as <strong>{roleLabel(previewRole)}</strong>. This action
                  will write to the live database exactly as if performed normally.
                </p>
                {current && (
                  <p className="mt-2 text-xs font-mono text-muted-foreground break-all">
                    {current.info.method} {current.info.path}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => answer(false)}
              data-testid="preview-write-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => answer(true)}
              className="bg-avenue-red text-white hover:bg-avenue-electric-red"
              data-testid="preview-write-submit"
            >
              Submit Change
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
